import * as React from 'react';
import { format } from 'date-fns';
import { ChevronDownIcon } from 'lucide-react';

import { ErrorField } from '@/components/error-field';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Field, FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { cn } from '@/lib/utils';

import { RequiredLabel } from '../required-label';

interface DateTimePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  time?: string; // "HH:mm"
  onTimeChange?: (time: string) => void;
  dateId?: string;
  timeId?: string;
  disabled?: boolean;
  dateAriaInvalid?: boolean;
  timeAriaInvalid?: boolean;
  className?: string;
}

export function DatePickerTime({
  date,
  onDateChange,
  time,
  onTimeChange,
  dateId = 'date-picker',
  timeId = 'time-picker',
  disabled = false,
  dateAriaInvalid = false,
  timeAriaInvalid = false,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <FieldGroup className={cn('flex-row', className)}>
      <Field className="w-[60%]" aria-invalid={dateAriaInvalid ? true : undefined}>
        <RequiredLabel htmlFor={dateId}>Date</RequiredLabel>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id={dateId}
              disabled={disabled}
              aria-invalid={dateAriaInvalid ? true : undefined}
              className="w-full justify-between font-normal"
            >
              {date ? (
                format(date, 'MM/dd/yyyy')
              ) : (
                <span className="text-muted-foreground">Select date</span>
              )}
              <ChevronDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              defaultMonth={date}
              onSelect={d => {
                onDateChange?.(d);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
        <ErrorField name={dateId} />
      </Field>

      <Field className="w-[40%]" aria-invalid={timeAriaInvalid ? true : undefined}>
        <RequiredLabel htmlFor={timeId}>Time</RequiredLabel>
        <Input
          type="time"
          id={timeId}
          step="60"
          disabled={disabled}
          aria-invalid={timeAriaInvalid ? true : undefined}
          value={time ?? ''}
          onChange={e => onTimeChange?.(e.target.value)}
          className="appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
        <ErrorField name={timeId} />
      </Field>
    </FieldGroup>
  );
}
